import { subject } from '@casl/ability';
import defineAbilityFor from 'services/authorization/roles';

import {
  SurfaceProtocolNode,
  SubSurfaceNode,
  SurfaceNodeUnion,
  SurfaceNodePointsTo,
  SurfaceRole,
  SurfaceBackgroundNode,
} from 'graphql/types';

export enum SurfaceNodeActions {
  all = 'manage',
  read = 'read',
  create = 'CREATE',
  delete = 'DELETE',
  deleteOwned = 'DELETE_OWNED',
  update = 'UPDATE',
  link = 'LINK',
  unLink = 'UNLINK',
  unLinkOwned = 'UNLINK_OWNED',
  copy = 'COPY',
  cut = 'CUT',
  paste = 'PASTE',
  updatePosition = 'UPDATE_POSITION',
  addToPlaceholder = 'ADD_TO_PLACEHOLDER',
  removeFromPlaceholder = 'REMOVE_FROM_PLACEHOLDER',
  manageCollaborators = 'MANAGE_COLLABORATORS',
  shareSurfaceNode = 'SHARE_SURFACE_NODE',
}

export interface ActionInput {
  surfaceNode: SurfaceNodeUnion;
  user: string;
  role?: string | null;
}

export interface UnLinkActionInput {
  link: SurfaceNodePointsTo;
  user: string;
  role?: string | null;
}

export interface PlaceholderActionInput {
  surfaceNode: SurfaceProtocolNode;
  user: string;
  role?: string | null;
}

export type SurfaceNodePermissionCheck<Input> = (input: Input) => boolean;

function isOwnerOrAdminOfParentSurface(surfaceNode: SurfaceNodeUnion): boolean {
  return false;
  /*const surface =
    surfaceNode.__typename === 'SubSurfaceNode'
      ? surfaceNode
      : surfaceNode.surface;

  // @ts-ignore
  return surface.loggedUserOwnerOrAdminOfParentSurface;*/
}

export function canReadSurfaceNode({
  surfaceNode,
  user,
  role,
}: ActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    return permissions.can(
      SurfaceNodeActions.read,
      subject('SurfaceNode', { ...surfaceNode }),
    );

    // return canRead || isOwnerOrAdminOfParentSurface(surfaceNode)
  }

  return /*isOwnerOrAdminOfParentSurface(surfaceNode) ||*/ false;
}

export function canDeleteSurfaceNode({
  surfaceNode,
  user,
  role,
}: ActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    const surface =
      surfaceNode.__typename === 'SubSurfaceNode'
        ? surfaceNode
        : (surfaceNode.surface as SubSurfaceNode);

    const canDelete = permissions.can(
      SurfaceNodeActions.delete,
      subject('Surface', { ...surface }),
    );

    return canDelete || isOwnerOrAdminOfParentSurface(surfaceNode);
  }

  return isOwnerOrAdminOfParentSurface(surfaceNode);
}

export function canDeleteOwnedSurfaceNode({
  surfaceNode,
  user,
  role,
}: ActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    return permissions.can(
      SurfaceNodeActions.deleteOwned,
      subject('SurfaceNode', { ...surfaceNode }),
    );
  }

  return isOwnerOrAdminOfParentSurface(surfaceNode);
}

export function canCreateSurfaceNode({
  surfaceNode,
  user,
  role,
}: ActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    return permissions.can(
      SurfaceNodeActions.create,
      subject('SurfaceNode', { ...surfaceNode }),
    );
  }

  return isOwnerOrAdminOfParentSurface(surfaceNode);
}

export function canUpdateSurfaceNode({
  surfaceNode,
  user,
  role,
}: ActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    return permissions.can(
      SurfaceNodeActions.update,
      subject('SurfaceNode', { ...surfaceNode }),
    );
  }

  return isOwnerOrAdminOfParentSurface(surfaceNode);
}

export function canLinkSurfaceNode({
  surfaceNode,
  user,
  role,
}: ActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    return permissions.can(
      SurfaceNodeActions.link,
      subject('Surface', { ...surfaceNode }),
    );
  }

  return isOwnerOrAdminOfParentSurface(surfaceNode);
}

export function canUnLinkSurfaceNode({
  link,
  user,
  role,
}: UnLinkActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    return permissions.can(
      SurfaceNodeActions.unLink,
      subject('SurfaceNodePointsTo', { ...link }),
    );
  }

  return false;

  //return isOwnerOrAdminOfParentSurface(surfaceNode);
}

export function canCopySurfaceNode({
  surfaceNode,
  user,
  role,
}: ActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    return permissions.can(
      SurfaceNodeActions.copy,
      subject('SurfaceNode', { ...surfaceNode }),
    );
  }

  return isOwnerOrAdminOfParentSurface(surfaceNode);
}

export function canRepositionSurfaceNode({
  surfaceNode,
  user,
  role,
}: ActionInput): boolean {
  if (surfaceNode.__typename === 'SurfaceBackgroundNode') {
    const s = surfaceNode as SurfaceBackgroundNode;
    if (s.isFixed) {
      return false;
    }
  }
  // if(surfaceNode instanceof SurfaceBackgroundNode){

  //   if(surfaceNode.isFixed){
  //     return false
  //   }
  // }
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    return permissions.can(
      SurfaceNodeActions.updatePosition,
      subject('SurfaceNode', { ...surfaceNode }),
    );
  }

  return isOwnerOrAdminOfParentSurface(surfaceNode);
}

export function canPasteSurfaceNode({
  surfaceNode,
  user,
  role,
}: ActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    return permissions.can(
      SurfaceNodeActions.create,
      subject('SurfaceNode', { ...surfaceNode }),
    );
  }

  return isOwnerOrAdminOfParentSurface(surfaceNode);
}

export function canCutSurfaceNode({
  surfaceNode,
  user,
  role,
}: ActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });
    const permissionSubject = subject('SurfaceNode', { ...surfaceNode });

    const canDelete = permissions.can(
      SurfaceNodeActions.delete,
      subject('Surface', { ...surfaceNode }),
    );

    const canDeleteOwned = permissions.can(
      SurfaceNodeActions.deleteOwned,
      permissionSubject,
    );

    return canDelete || canDeleteOwned;
  }

  return isOwnerOrAdminOfParentSurface(surfaceNode);
}

export function canAddToPlaceholderNode({
  surfaceNode,
  user,
  role,
}: PlaceholderActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    return permissions.can(
      SurfaceNodeActions.addToPlaceholder,
      subject('SurfaceNode', { ...surfaceNode }),
    );
  }

  return isOwnerOrAdminOfParentSurface(surfaceNode);
}

export function canRemoveFromPlaceholderNode({
  surfaceNode,
  user,
  role,
}: PlaceholderActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    return permissions.can(
      SurfaceNodeActions.removeFromPlaceholder,
      subject('SurfaceNode', { ...surfaceNode }),
    );
  }

  return isOwnerOrAdminOfParentSurface(surfaceNode);
}

export function canManageCollaboratorsOnSurfaceNode({
  surfaceNode,
  user,
  role,
}: ActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    const canManageCollaborators = permissions.can(
      SurfaceNodeActions.manageCollaborators,
      subject('SurfaceNode', { ...surfaceNode }),
    );

    return canManageCollaborators || isOwnerOrAdminOfParentSurface(surfaceNode);
  }

  return isOwnerOrAdminOfParentSurface(surfaceNode);
}

export function canShareSurfaceNode({
  surfaceNode,
  user,
  role,
}: ActionInput): boolean {
  if (role) {
    const permissions = defineAbilityFor({ role, user });

    const canShare = permissions.can(
      SurfaceNodeActions.shareSurfaceNode,
      subject('SurfaceNode', { ...surfaceNode }),
    );

    return canShare || isOwnerOrAdminOfParentSurface(surfaceNode);
  }

  return isOwnerOrAdminOfParentSurface(surfaceNode);
}
